import type { ProjectFunnelDto } from '@nsb/core';

/**
 * The top of the funnel's price scale, as both drawings set it: the highest figure drawn (the band and what runs over
 * it, what is spent, the client's budget, its cap), with a little room over it.
 */
export function priceTop(f: Pick<ProjectFunnelDto, 'band' | 'spent' | 'budget' | 'ceiling'>): number {
  return Math.max(1, ...f.band.map((p) => p.highCents + (p.overCents ?? 0)), f.spent.at(-1)?.cumulativeCents ?? 0, f.budget?.highCents ?? 0, f.ceiling?.totalCents ?? 0) * 1.08;
}

/**
 * Where the funnel's price scale starts, and the share of the drawing's height it keeps (#129; Jason, 2026-10-29:
 * "remove the blank white space below the funnel and time line and only expand as the size of the funnel requires"):
 * a round figure under the lowest figure drawn (the band's lows and the client's budget's; never what is spent, which is
 * drawn from there up), a fifth of the top under it so the band's low figure is printed clear of the estimate's marker
 * on the timeline, and the drawing that much shorter, so a band keeps its true height against the price. With no
 * estimate, or where it would save less than a tenth of the height, the scale starts at zero as it always did.
 */
export function funnelFloor(f: Pick<ProjectFunnelDto, 'band' | 'spent' | 'budget' | 'ceiling'>): { floorCents: number; share: number } {
  if (!f.band.length) return { floorCents: 0, share: 1 };
  const top = priceTop(f);
  const lowest = Math.min(...f.band.map((p) => p.lowCents), f.budget?.lowCents ?? Infinity);
  const under = lowest - top * 0.2;
  if (under <= 0) return { floorCents: 0, share: 1 };
  // Down to a multiple of half its leading power of ten: $355,600 to $350,000, $27,040 to $25,000.
  const step = 10 ** Math.floor(Math.log10(under)) / 2;
  const floorCents = Math.floor(under / step) * step;
  const share = (top - floorCents) / top;
  if (share > 0.9) return { floorCents: 0, share: 1 };
  return { floorCents, share: Math.max(0.5, share) };
}

/** How far the land's paper runs empty under the land, let down under the timeline so the land meets it. */
const LAND_FOOT = 0.1;

/**
 * Where the land through the seasons is laid under the funnel across: from just under the drawing's top, its paper's
 * empty foot let down under the timeline (`LAND_FOOT`), so the painting fills the drawing whatever its height and is
 * never cut at the top (Jason, 2026-10-07: "never clipped on the top"); stretched to it as it is stretched to its dates.
 */
export function landBand(top: number, axis: number): { y: number; height: number } {
  return { y: top + 1, height: (axis - top - 1) / (1 - LAND_FOOT) };
}
